import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Dialog, DialogActions, DialogContent, Divider } from '@mui/material';
import CustomButton from 'lib/designSystem/Button';
import DialogTitle from 'lib/designSystem/Title/DialogTitle';
import CustomToggleButtonGroup from 'lib/designSystem/ToggleButtonGroup';
import { formatDateBack } from 'lib/functions/utils';
import React, { useCallback, useEffect, useState } from 'react';

import DocumentsTable from './DocumentsTable';
import ExcelUpload from './ExcelUpload';
import ManualForm, { getEmptyDocument } from './ManualForm';
import type { DocumentImporterProps, ImporterDocumentRow } from './types';

const TAB_MANUAL = 'manual';
const TAB_EXCEL = 'excel';

const DocumentImporter: React.FC<DocumentImporterProps> = ({
  open,
  onClose,
  config,
  business,
  onImport,
}) => {
  const [documents, setDocuments] = useState<ImporterDocumentRow[]>([]);
  const [activeTab, setActiveTab] = useState(TAB_MANUAL);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [formDocument, setFormDocument] = useState<ImporterDocumentRow>(() =>
    getEmptyDocument(business.identifier),
  );
  const [loading, setLoading] = useState(false);

  const resetOnClose = useCallback(() => {
    setDocuments([]);
    setActiveTab(TAB_MANUAL);
    setEditIndex(null);
    setFormDocument(getEmptyDocument(business.identifier));
  }, [business.identifier]);

  useEffect(() => {
    if (!open) resetOnClose();
  }, [open, resetOnClose]);

  const handleAdd = useCallback(
    (doc: ImporterDocumentRow) => {
      setDocuments((prev) => [...prev, doc]);
      setFormDocument(getEmptyDocument(business.identifier));
      setEditIndex(null);
    },
    [business.identifier],
  );

  const handleUpdate = useCallback(
    (doc: ImporterDocumentRow) => {
      if (editIndex === null) return;
      setDocuments((prev) => prev.map((d, i) => (i === editIndex ? doc : d)));
      setFormDocument(getEmptyDocument(business.identifier));
      setEditIndex(null);
    },
    [editIndex, business.identifier],
  );

  const handleCancelEdit = useCallback(() => {
    setEditIndex(null);
    setFormDocument(getEmptyDocument(business.identifier));
  }, [business.identifier]);

  const handleEdit = useCallback(
    (index: number) => {
      setActiveTab(TAB_MANUAL);
      const row = documents[index];
      let issuedDate = row.issuedDate;
      if (/^\d{8}$/.test(issuedDate)) {
        issuedDate = `${row.issuedDate.slice(4, 8)}-${row.issuedDate.slice(2, 4)}-${row.issuedDate.slice(0, 2)}`;
      } else if (issuedDate && !/^\d{4}-\d{2}-\d{2}$/.test(issuedDate)) {
        issuedDate = formatDateBack(issuedDate) || issuedDate;
      }
      setFormDocument({ ...row, issuedDate });
      setEditIndex(index);
    },
    [documents],
  );

  const handleDelete = useCallback(
    (index: number) => {
      setDocuments((prev) => prev.filter((_, i) => i !== index));
      if (editIndex === index) {
        setEditIndex(null);
        setFormDocument(getEmptyDocument(business.identifier));
      } else if (editIndex != null && editIndex > index) {
        setEditIndex(editIndex - 1);
      }
    },
    [editIndex, business.identifier],
  );

  const handleAddExcelRows = useCallback((rows: ImporterDocumentRow[]) => {
    setDocuments((prev) => [...prev, ...rows]);
  }, []);

  const handleImport = useCallback(async () => {
    if (documents.length === 0) return;
    setLoading(true);
    try {
      await onImport(documents);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [documents, onImport]);

  const existingFolios = documents.map((d) => d.folio.trim()).filter(Boolean);

  return (
    <Dialog maxWidth="md" fullWidth open={open} onClose={onClose}>
      <DialogTitle
        title={config.title}
        icon={<FileUploadIcon fontSize="small" color="primary" />}
        color="primary"
        onClose={onClose}
        noMargin
      />
      <DialogContent>
        {config.allowExcelUpload && (
          <CustomToggleButtonGroup
            value={activeTab}
            onChange={setActiveTab}
            options={[
              { value: TAB_MANUAL, label: 'Entrada manual' },
              { value: TAB_EXCEL, label: 'Carga desde Excel' },
            ]}
            sx={{
              mb: 2,
              width: '100%',

              '& .MuiToggleButtonGroup-grouped': {
                flex: 1,
              },
            }}
          />
        )}

        {(activeTab === TAB_MANUAL || !config.allowExcelUpload) && (
          <div id="doc-importer-panel-manual" role="tabpanel">
            <ManualForm
              config={config}
              businessIdentifier={business.identifier}
              formDocument={formDocument}
              setFormDocument={setFormDocument}
              editIndex={editIndex}
              onAdd={handleAdd}
              onUpdate={handleUpdate}
              onCancelEdit={handleCancelEdit}
              existingFolios={existingFolios}
            />
          </div>
        )}

        {activeTab === TAB_EXCEL && (
          <div id="doc-importer-panel-excel" role="tabpanel">
            <ExcelUpload
              config={config}
              businessIdentifier={business.identifier}
              onAddRows={handleAddExcelRows}
            />
          </div>
        )}

        <DocumentsTable
          documents={documents}
          config={config}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </DialogContent>
      <Divider sx={{ my: 1 }} variant="middle" />
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <CustomButton
          variant="contained"
          color="primary"
          disabled={documents.length === 0 || loading}
          onClick={() => void handleImport()}
        >
          Guardar {`(${documents.length})`}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentImporter;
export { getImporterConfigForCategory } from './configs';
export type {
  DocumentImporterConfig,
  DocumentImporterProps,
  ImporterDocumentRow,
} from './types';
